import { create } from 'zustand';
import { adminProductImagesManagementApi } from '@api/api-config';
import { AdminProductListDTO } from '@api/generated/portal-be-admin';

interface ProductImageListState {
  // Seznam produktů
  products: AdminProductListDTO[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;

  // Loading a error stavy
  isLoading: boolean;
  error: string | null;

  // Filtry
  filters: {
    query?: string;
    categoryEcid?: string;
    hasImages?: boolean;
  };
}

interface ProductImageListActions {
  // Načtení seznamu produktů
  fetchProducts: (params?: { query?: string; categoryEcid?: string; hasImages?: boolean; page?: number; size?: number }) => Promise<void>;

  // Nastavení filtrů
  setFilters: (filters: Partial<ProductImageListState['filters']>) => void;
  clearFilters: () => void;

  // Utility
  clearError: () => void;
}

export const useProductImageListStore = create<ProductImageListState & ProductImageListActions>((set, get) => ({
  // Initial state
  products: [],
  totalElements: 0,
  totalPages: 0,
  currentPage: 1,
  pageSize: 20,
  isLoading: false,
  error: null,
  filters: {},

  // Actions
  fetchProducts: async (params = {}) => {
    set({ isLoading: true, error: null });

    try {
      const { filters } = get();

      const response = await adminProductImagesManagementApi.getProductList({
        query: params.query ?? filters.query,
        categoryEcid: params.categoryEcid ?? filters.categoryEcid,
        hasImages: params.hasImages ?? filters.hasImages,
        page: params.page ?? 1, // Vždy použij 1 pro API, pokud není specifikováno jinak
        size: params.size ?? get().pageSize,
      });

      set({
        products: response.data.products || [],
        totalElements: response.data.totalElements || 0,
        totalPages: response.data.totalPages || 0,
        currentPage: response.data.currentPage || 1, // API je 1-based, store má být 1-based
        pageSize: response.data.size || 20,
        isLoading: false,
        error: null,
      });
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nepodařilo se načíst produkty';
      set({
        error: errorMessage,
        isLoading: false,
      });
    }
  },

  setFilters: newFilters => {
    set(state => ({
      filters: { ...state.filters, ...newFilters },
    }));
  },

  clearFilters: () => {
    set({
      filters: {
        query: '',
        categoryEcid: '',
        hasImages: undefined,
      },
    });
  },

  clearError: () => {
    set({ error: null });
  },
}));
